import React, { useEffect } from 'react';
import {
	Box,
	Divider,
	ListItemIcon,
	Menu,
	MenuItem,
	TextField,
} from '@mui/material';
import { legends } from './utils.RiskMatrix';
import { getGradeLabel } from '@Utils/gradeLabels';
import DoneSharpIcon from '@mui/icons-material/DoneSharp';
import StarIcon from '@mui/icons-material/Star';
import {
	Button,
	InfoCard,
	StylovyzeComposableDialog,
	useGlobalization,
} from '@innovyze/stylovyze';
import { useRiskMatrixStyles } from './RiskMatrixStyles';
import { useDispatch } from 'react-redux';
import {
	setDidMatrixUpdate,
	setOnlyGradesUpdated,
	setRiskSetupDirty,
	setUpdatedRanges,
} from '@Actions/Risk.actions';

interface RiskLegendMenuProps {
	anchorEl: HTMLElement | null;
	open: boolean;
	selectedGrade: string;
	handleClose(): void;
	handleGradeChange(grade: string): void;
}

export const RiskLegendMenu = ({
	anchorEl,
	open,
	handleClose,
	selectedGrade,
	handleGradeChange,
}: RiskLegendMenuProps) => {
	const { t } = useGlobalization();
	const classes = useRiskMatrixStyles();

	return (
		<Menu
			anchorEl={anchorEl}
			id="risk-legend-menu"
			open={open}
			onClose={handleClose}
			onClick={handleClose}
			MenuListProps={{
				'aria-labelledby': 'gridcell-button',
			}}
			PaperProps={{
				elevation: 0,
				sx: {
					overflow: 'visible',
					filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
					minWidth: '183px',
					mt: 1.5,
					'& .Mui-selected': {
						backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
					},
				},
			}}
			transformOrigin={{ horizontal: 'right', vertical: 'top' }}
			anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
			{legends.map((legend, key) => (
				<MenuItem
					key={key}
					value={selectedGrade}
					onClick={() => handleGradeChange(legend.name)}
					selected={
						selectedGrade.toUpperCase() ===
						legend.name.toUpperCase()
					}>
					<div className={classes.gridCellMenuItemContainer}>
						<div className={classes.gridCellMenuItemIconContainer}>
							<ListItemIcon>
								<StarIcon
									style={{
										width: '21px',
										color: legend.color,
									}}
								/>
							</ListItemIcon>
						</div>
						<div className={classes.gridCellMenuItemLegendName}>
							{getGradeLabel(legend.name, t)}
						</div>
						{selectedGrade.toUpperCase() ===
						legend.name.toUpperCase() ? (
							<div
								className={
									classes.gridCellMenuItemDoneIconContainer
								}>
								<DoneSharpIcon
									className={classes.gridCellMenuItemDoneIcon}
								/>
							</div>
						) : (
							<></>
						)}
					</div>
				</MenuItem>
			))}
		</Menu>
	);
};

interface RangeEditorDialogrProps {
	showEditRangesDialog: boolean;
	setShowEditRangesDialog: React.Dispatch<React.SetStateAction<boolean>>;
	cofRangeList: number[];
	lofRangeList: number[];
}
export const RangeEditorDialog = ({
	showEditRangesDialog,
	setShowEditRangesDialog,
	cofRangeList,
	lofRangeList,
}: RangeEditorDialogrProps) => {
	const { t } = useGlobalization();
	const classes = useRiskMatrixStyles();
	const dispatch = useDispatch();
	const [disableSaveBtn, setDisableSaveBtn] = React.useState(true);

	return (
		<StylovyzeComposableDialog
			width="700px"
			className={classes.dialogBoxStyle}
			open={showEditRangesDialog}
			onCancel={() => setShowEditRangesDialog(false)}
			title={t('Change Ranges')}
			footer={
				<>
					<Button
						variant="contained"
						color="secondary"
						onClick={() => setShowEditRangesDialog(false)}>
						Cancel
					</Button>
					<Button
						disabled={disableSaveBtn}
						variant="contained"
						color="primary"
						onClick={() => {
							dispatch(
								setUpdatedRanges({
									lofRanges: [...lofRangeList],
									cofRanges: [...cofRangeList],
								}),
							);
							dispatch(setOnlyGradesUpdated(false));
							dispatch(setDidMatrixUpdate(true));
							dispatch(setRiskSetupDirty(true));
							setDisableSaveBtn(false);
							setShowEditRangesDialog(false);
						}}>
						{t('OK')}
					</Button>
				</>
			}>
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					columnGap: '24px',
				}}>
				<RangeEditor
					rangeName="lof"
					rangeList={lofRangeList}
					setDisableSaveBtn={setDisableSaveBtn}
				/>
				<RangeEditor
					rangeName="cof"
					rangeList={cofRangeList}
					setDisableSaveBtn={setDisableSaveBtn}
				/>
			</div>
		</StylovyzeComposableDialog>
	);
};

interface RangeEditorProps {
	rangeName: string;
	rangeList: number[];
	setDisableSaveBtn: React.Dispatch<React.SetStateAction<boolean>>;
}
const RangeEditor = ({
	rangeName,
	rangeList,
	setDisableSaveBtn,
}: RangeEditorProps) => {
	const stepValue = 0.01;
	const arr = [...rangeList];
	const classes = useRiskMatrixStyles();
	const { t } = useGlobalization();

	const getMinValue = (val: number) => {
		return (val + 0.01).toFixed(2);
	};

	const getMaxValue = (val: number) => {
		return (val - 0.01).toFixed(2);
	};

	const [val1, setVal1] = React.useState(arr[0].toString());
	const [val2, setVal2] = React.useState(arr[1].toString());
	const [val3, setVal3] = React.useState(arr[2].toString());
	const [val4, setVal4] = React.useState(arr[3].toString());
	const [val5, setVal5] = React.useState(arr[4].toString());

	const checkConditions = () => {
		if (
			parseFloat(val1) > parseFloat('0') &&
			parseFloat(val1) < parseFloat(val2) &&
			parseFloat(val2) < parseFloat(val3) &&
			parseFloat(val3) < parseFloat(val4) &&
			parseFloat(val4) < parseFloat(val5)
		) {
			return true;
		} else return false;
	};

	const switchBtn = () => {
		if (checkConditions()) setDisableSaveBtn(false);
		else setDisableSaveBtn(true);
	};

	useEffect(() => {
		switchBtn();
	}, [val1, val2, val3, val4, val5]);

	useEffect(() => {
		setDisableSaveBtn(true);
	}, []);

	return (
		<div className={classes.contentBody}>
			<InfoCard
				dataCy={`range-editor-inforcard-${rangeName}`}
				title={
					<>
						{rangeName === 'lof' ? (
							<div style={{ display: 'block' }}>
								<div>{t('LoF')}</div>
								<div style={{ fontWeight: 400 }}>
									{t('(Likelihood of Failure)')}
								</div>
							</div>
						) : (
							<div style={{ display: 'block' }}>
								<div>{t('CoF')}</div>
								<div style={{ fontWeight: 400 }}>
									{t('(Consequence of Failure)')}
								</div>
							</div>
						)}
					</>
				}>
				<div style={{ width: '265px' }} id="range-editor-textbox-list">
					<Box
						sx={{
							'& .MuiTextField-root': { m: 1, width: '125px' },
						}}>
						<div>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
								}}>
								<span style={{ margin: '0 20px 0 5px' }}>
									<span>&le;</span>
								</span>
								<TextField
									size="small"
									id="outlined-number"
									type="number"
									error={
										parseFloat(val1) < parseFloat('0.01') ||
										parseFloat(val1) >= parseFloat(val2)
									}
									InputLabelProps={{
										shrink: true,
									}}
									InputProps={{
										inputProps: {
											min: stepValue,
											max: getMaxValue(parseFloat(val2)),
											step: stepValue,
										},
									}}
									value={val1}
									onChange={e => {
										setVal1(e.target.value);
									}}
									onBlur={e => {
										const val = e.target.value;
										rangeList[0] = +val;
									}}
								/>
							</div>
							<Divider className={classes.rangeEditorDivider} />
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
								}}>
								<span style={{ margin: '0 20px 0 5px' }}>
									<span>&le;</span>
								</span>
								<TextField
									size="small"
									id="outlined-number"
									type="number"
									error={
										parseFloat(val2) <= parseFloat(val1) ||
										parseFloat(val2) >= parseFloat(val3)
									}
									InputLabelProps={{
										shrink: true,
									}}
									InputProps={{
										inputProps: {
											min: getMinValue(parseFloat(val1)),
											max: getMaxValue(parseFloat(val3)),
											step: stepValue,
										},
									}}
									value={val2}
									onChange={e => {
										setVal2(e.target.value);
									}}
									onBlur={e => {
										const val = e.target.value;
										rangeList[1] = +val;
									}}
								/>
							</div>
							<Divider className={classes.rangeEditorDivider} />
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
								}}>
								<span style={{ margin: '0 20px 0 5px' }}>
									<span>&le;</span>
								</span>
								<TextField
									size="small"
									id="outlined-number"
									type="number"
									error={
										parseFloat(val3) <= parseFloat(val2) ||
										parseFloat(val3) >= parseFloat(val4)
									}
									InputLabelProps={{
										shrink: true,
									}}
									InputProps={{
										inputProps: {
											min: getMinValue(parseFloat(val2)),
											max: getMaxValue(parseFloat(val4)),
											step: stepValue,
										},
									}}
									value={val3}
									onChange={e => {
										setVal3(e.target.value);
									}}
									onBlur={e => {
										const val = e.target.value;
										rangeList[2] = +val;
									}}
								/>
							</div>
							<Divider className={classes.rangeEditorDivider} />
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
								}}>
								<span style={{ margin: '0 20px 0 5px' }}>
									<span>&le;</span>
								</span>
								<TextField
									size="small"
									id="outlined-number"
									type="number"
									error={
										parseFloat(val4) <= parseFloat(val3) ||
										parseFloat(val4) > parseFloat(val5)
									}
									InputLabelProps={{
										shrink: true,
									}}
									InputProps={{
										inputProps: {
											min: getMinValue(parseFloat(val3)),
											max: getMaxValue(parseFloat(val5)),
											step: stepValue,
										},
									}}
									value={val4}
									onChange={e => {
										setVal4(e.target.value);
									}}
									onBlur={e => {
										const val = e.target.value;
										rangeList[3] = +val;
									}}
								/>
							</div>
							<Divider className={classes.rangeEditorDivider} />
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
								}}>
								<span style={{ margin: '0 20px 0 5px' }}>
									<span>&le;</span>
								</span>
								<TextField
									size="small"
									id="outlined-number"
									type="number"
									error={parseFloat(val5) <= parseFloat(val4)}
									InputLabelProps={{
										shrink: true,
									}}
									InputProps={{
										inputProps: {
											min: getMinValue(parseFloat(val4)),
											step: stepValue,
										},
									}}
									value={val5}
									onChange={e => {
										setVal5(e.target.value);
									}}
									onBlur={e => {
										const val = e.target.value;
										rangeList[4] = +val;
									}}
								/>
							</div>
						</div>
					</Box>
				</div>
			</InfoCard>
		</div>
	);
};
